import { Box, List, TableCell, TableCellProps } from "@mui/material";
import { ReactNode } from "react";

import InfoMessage from "../info-message";
import { StyledListItem, StyledTypography } from "../styled-components";

export const NOT_PROVIDED = <i>Not Provided</i>;
export const INCONSISTENT_DATA = "inconsistentData";

const TextFieldWithTooltip = ({
  children = null,
  text = "",
  tooltipMsg = "",
}) => (
  <StyledListItem justifyContent="center">
    <InfoMessage tooltipMsg={tooltipMsg}>{children ?? text}</InfoMessage>
  </StyledListItem>
);

export const InconsistentDataCell = () => (
  <TextFieldWithTooltip
    text="-"
    tooltipMsg="Not able to be displayed due to missing or inconsistent data"
  />
);

const DisplayValue = ({ value }: { value: string | null }) => {
  if (value === INCONSISTENT_DATA) {
    return <InconsistentDataCell />;
  }
  return (
    <StyledTypography align="center" whiteSpace="pre-wrap">
      {value || NOT_PROVIDED}
    </StyledTypography>
  );
};

type CoverageCellProps = {
  value: string | null;
  colSpan?: number;
  customRender?: React.ReactNode;
};

const CoverageCell = ({
  value,
  colSpan = 1,
  customRender,
}: CoverageCellProps) => (
  <TableCell colSpan={colSpan}>
    {customRender ?? <DisplayValue value={value || ""} />}
  </TableCell>
);

type CoverageTitleCellProps = {
  label: string;
};

const CoverageTitleCell = ({ label }: CoverageTitleCellProps) => (
  <TableCell>
    <StyledTypography align="left">{label}</StyledTypography>
  </TableCell>
);

type CoverageVectorCellProps = {
  value: (string | null)[];
  colSpan?: number;
};

const CoverageVectorCell = ({
  value,
  colSpan = 1,
}: CoverageVectorCellProps) => (
  <TableCell colSpan={colSpan}>
    <Box display="flex" gap={2} alignItems="center">
      {value.map((c, i) => (
        <Box key={i} flex={1}>
          <DisplayValue value={c} />
        </Box>
      ))}
    </Box>
  </TableCell>
);

type CoverageMultiVectorCellProps = {
  value: (string | null)[][];
  colSpan?: number;
};

const CoverageMultiVectorCell = ({
  value,
  colSpan = 1,
}: CoverageMultiVectorCellProps) => (
  <TableCell colSpan={colSpan}>
    <Box display="flex" gap={2} alignItems="center">
      {value.map((c, i) => (
        <Box key={i} flex={1} display="flex" gap={2} alignItems="center">
          {Array.isArray(c) ? (
            c.map((cc, j) => (
              <Box key={j} flex={1}>
                <DisplayValue value={cc} />
              </Box>
            ))
          ) : (
            <Box key={i} flex={1}>
              <DisplayValue value={c} />
            </Box>
          )}
        </Box>
      ))}
    </Box>
  </TableCell>
);

type CoverageMultiVectorHeaderCellProps = {
  labels: (string | null)[][];
  fontWeight?: "bold" | "normal";
  colspan?: number;
};

const CoverageMultiVectorHeaderCell = ({
  labels,
  fontWeight = "bold",
  colspan = 1,
}: CoverageMultiVectorHeaderCellProps) => (
  <TableCell colSpan={colspan}>
    <Box display="flex" gap={2}>
      {labels.map((c, i) => (
        <Box key={i} flex={1} display="flex" gap={2}>
          {Array.isArray(c) ? (
            c.map((cc, j) => (
              <Box key={j} flex={1}>
                <StyledTypography
                  fontWeight={fontWeight}
                  align="center"
                  variant="body2"
                >
                  {cc}
                </StyledTypography>
              </Box>
            ))
          ) : (
            <Box key={i} flex={1}>
              <StyledTypography
                fontWeight={fontWeight}
                align="center"
                variant="body2"
              >
                {c}
              </StyledTypography>
            </Box>
          )}
        </Box>
      ))}
    </Box>
  </TableCell>
);

type CoverageVectorHeaderCellProps = {
  label: string[];
  fontWeight?: "bold" | "normal";
  colspan?: number;
};

const CoverageVectorHeaderCell = ({
  label,
  fontWeight = "bold",
  colspan = 1,
}: CoverageVectorHeaderCellProps) => (
  <TableCell colSpan={colspan}>
    <Box display="flex" gap={2}>
      {label.map((c, i) => (
        <Box key={i} flex={1}>
          <StyledTypography
            fontWeight={fontWeight}
            align="center"
            variant="body2"
          >
            {c}
          </StyledTypography>
        </Box>
      ))}
    </Box>
  </TableCell>
);

const CoverageHeaderCell = ({ label }: CoverageTitleCellProps) => (
  <TableCell>
    <StyledTypography fontWeight="bold" align="center" variant="body2">
      {label}
    </StyledTypography>
  </TableCell>
);

type CustomCellProps = {
  children?: ReactNode;
  hasChilds?: boolean;
  isSticky?: boolean;
  sx?: object;
  boxProps?: object;
  align?: "center" | "left" | "right" | "inherit" | "justify";
  colSpan?: number;
};

const stickyStyle = {
  position: "sticky",
  left: 0,
  zIndex: 2,
};

const CustomCell = ({
  children,
  hasChilds = false,
  isSticky = false,
  sx,
  boxProps,
  align,
  colSpan,
  ...rest
}: CustomCellProps) => {
  const cellProps: TableCellProps = {
    ...rest,
    padding: "none",
    ...(align ? { align } : {}),
    ...(colSpan ? { colSpan } : {}),
    sx: {
      ...(isSticky ? stickyStyle : {}),
      ...(hasChilds ? {} : { padding: "1rem" }),
      ...sx,
    },
  };
  return (
    <TableCell {...cellProps}>
      {hasChilds ? (
        <Box component={List} dense display="flex" {...boxProps} p={2}>
          {children}
        </Box>
      ) : (
        children
      )}
    </TableCell>
  );
};

export {
  CoverageCell,
  CoverageHeaderCell,
  CoverageMultiVectorCell,
  CoverageMultiVectorHeaderCell,
  CoverageTitleCell,
  CoverageVectorCell,
  CoverageVectorHeaderCell,
  CustomCell,
};
